/**
 * ATTEST-EVIDENCE-INTEGRITY-1a — who this letter will be signed as.
 *
 * There is no form here on purpose. The firm legal name, the CPA's name and the
 * state licence number are read from the active firm profile at generation
 * time; a text box for them would be a text box for typing somebody else's
 * licence number over a signature block.
 *
 * So this component's whole job is to *show* the CPA the identity the server
 * will use, before they spend a wizard on a letter that gets refused. It reads
 * the same `/v1/firm-profile` the profile editor writes, so what it shows is
 * what the letter will say.
 */
import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { apiClient } from '@/api/client'
import type { FirmProfile, FirmProfileResponse } from '@/api/sharedTypes'

type Load = 'loading' | 'ready' | 'error'

function licenceLine(profile: FirmProfile): string {
  const state = (profile.cpa_license_state ?? '').trim().toUpperCase()
  const number = (profile.cpa_license_no ?? '').trim()
  return state && number ? `${state} CPA License No. ${number}` : ''
}

function addressLine(profile: FirmProfile): string {
  const city = (profile.firm_address_city ?? '').trim()
  const state = (profile.firm_address_state ?? '').trim()
  const zip = (profile.firm_address_zip ?? '').trim()
  return [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')
}

export interface SigningIdentityCardProps {
  /**
   * Rendered beside the heading — the Independence step passes its "(i)" here
   * so the help sits on the card's own label rather than on a second one
   * invented above it (ATTEST-INDEPENDENCE-MATRIX-1).
   */
  help?: ReactNode
}

export function SigningIdentityCard({ help }: SigningIdentityCardProps = {}) {
  const [state, setState] = useState<Load>('loading')
  const [profile, setProfile] = useState<FirmProfile | null>(null)
  const [active, setActive] = useState(false)

  useEffect(() => {
    let live = true
    apiClient.get<FirmProfileResponse>('/v1/firm-profile')
      .then(({ data }) => {
        if (!live) return
        setProfile(data.profile)
        setActive(data.profile?.profile_status === 'active')
        setState('ready')
      })
      .catch(() => { if (live) setState('error') })
    return () => { live = false }
  }, [])

  if (state === 'loading') {
    return (
      <section className="rounded-lg border border-border p-4 text-sm text-muted-foreground">
        Loading the firm profile this letter will be signed under…
      </section>
    )
  }

  // A failed read is not a refusal — the server decides that when the letter is
  // generated. Say what is unknown rather than implying the letter is blocked.
  if (state === 'error') {
    return (
      <section
        data-testid="signing-identity-unknown"
        className="rounded-lg border border-border p-4 text-sm text-muted-foreground"
      >
        The firm profile could not be read just now, so the signature block
        cannot be shown here. The letter is still signed from the profile of
        record — <Link className="underline" to="/firm-profile">check it</Link> if
        the letter is refused.
      </section>
    )
  }

  if (!active) {
    return (
      <section
        data-testid="signing-identity-inactive"
        className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
      >
        <p className="font-semibold">No active firm profile</p>
        <p className="mt-1">
          A letter is signed under a firm legal name, a licensed CPA name and a
          state licence number of record — all taken from your firm profile,
          never typed into this form. Complete and activate the profile before
          generating: <Link className="underline" to="/firm-profile">Firm Profile</Link>.
        </p>
      </section>
    )
  }

  const licence = licenceLine(profile!)

  return (
    <section
      data-testid="signing-identity"
      className="rounded-lg border border-border p-4"
    >
      <h3 className="flex items-center justify-between gap-2 text-sm font-semibold">
        This letter will be signed as
        {help}
      </h3>
      <div className="mt-2 text-sm">
        <p data-testid="signing-firm">{profile!.firm_legal_name}</p>
        <p data-testid="signing-cpa">{profile!.cpa_name}</p>
        {licence && <p data-testid="signing-licence">{licence}</p>}
        {profile!.firm_address_street && (
          <p className="text-muted-foreground">{profile!.firm_address_street}</p>
        )}
        {addressLine(profile!) && (
          <p className="text-muted-foreground">{addressLine(profile!)}</p>
        )}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        Taken from your firm profile at generation time.{' '}
        <Link className="underline" to="/firm-profile">Change it there</Link> —
        it cannot be edited on a letter.
      </p>
    </section>
  )
}
